/**
 * SOLID Review
 * S — Strong. One concern: the 3D upper cup's motion — its pose (rest, peek, reveal, shake), its glow, whether it can be tapped, and
 *     whether the demand frame loop needs another frame.
 * O — Good. A new pose is one more target in `frame`; a new glow one more branch.
 * L — Strong. A view model (`IServiceViewModel`) with no state a view renders: its state is the frame loop's.
 * I — Good. One frame, the pointer's three moves, and the facts a view derives from its props.
 * D — Strong. The platform's cursor, and a colour maker the view hands it — three.js stays in the 3D chunk.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import type { IServicePageCursor } from './platform/IConfigDepViewPlatform';
import { GamePhase } from '../../application/ports/ClientVocabulary';
import {
  PEEK_PIVOT_Y, PEEK_PIVOT_Z, PEEK_TILT, PIVOT_REST_Y, PIVOT_REST_Z, REVEAL_LIFT,
} from '../components/game/cupGeometry';

/** The demand loop stops once every lerp is this close to its target. */
const EPS = 0.0005;

/** The cup scene's props, as the scene's core hands them (`IPropsCupScene`). */
export interface IPropsCupMotion {
  readonly phase: GamePhase;
  readonly isActiveRoller: boolean;
  readonly isShaking: boolean;
  readonly diceValues: { readonly die1: number; readonly die2: number } | null;
  readonly diceRevealed: boolean;
}

/** What the cup's view derives from its props: whether a tap does anything, the reveal, the roller's peek, the dice shown. */
export interface IStateCupFacts {
  readonly canTap: boolean;
  readonly isRevealing: boolean;
  readonly rollerExposed: boolean;
  readonly showDice: boolean;
}

/** The pivot group the frame moves — a three.js object's position and rotation. */
export interface IServiceCupPivot {
  readonly position: { x: number; y: number; z: number };
  readonly rotation: { x: number; z: number };
}

/** The cup's material the frame lights — a three.js standard material's emissive colour and intensity. */
export interface IServiceCupGlow<TColor> {
  readonly emissive: { lerp(target: TColor, alpha: number): unknown };
  emissiveIntensity: number;
}

const NO_STATE: Readonly<Record<string, never>> = Object.freeze({});

/**
 * THE 3D CUP'S MOTION (frontend-framework-oo P4, 2026-10-29, §28.296) — `GameScene.tsx`'s `CupScene`: what its frame callback and its
 * pointer handlers did, as methods its react-three-fiber view calls (the view keeps `useFrame` — a hard constraint — and hands every
 * frame here, so the per-frame code is testable). One per mounted cup scene: a fresh canvas starts the cup at rest.
 *
 * The pivot group is anchored at the cup's back-bottom rim; rotating it about X tips the cup up on that back edge while moving the
 * pivot lowers or raises that rim. Peek: tip the front up by `PEEK_TILT` and bring the rim down to the lower cup's top, opening the
 * front toward the roller. Reveal: lift clear of the scene with a spin. The canvas renders on demand, and a state change gives one
 * frame — not enough for the lerps to arrive — so every frame asks for the next while any animation is still moving.
 */
export class CupMotion<TColor> implements IServiceViewModel<typeof NO_STATE> {
  private static lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }

  /** The smoothed pose of the pivot — the frame loop's own state. */
  private readonly anim = { posY: PIVOT_REST_Y, posZ: PIVOT_REST_Z, tiltX: 0, spinZ: 0 };

  constructor(
    private readonly cursor: IServicePageCursor,
    private readonly colour: (hex: string) => TColor,
  ) {}

  /** Tappable: roll during ROLLING, or reveal during blind PEEKING; the reveal for REVEALING and GAME_OVER both (a last life lost
   *  jumps straight to GAME_OVER); the cup lifts for the roller only once they chose to peek; the dice show to the roller after the
   *  peek and to everyone at the reveal. */
  static factsOf(props: IPropsCupMotion): IStateCupFacts {
    const { phase, isActiveRoller, diceRevealed, diceValues } = props;
    const canTap = isActiveRoller && (phase === GamePhase.ROLLING || (phase === GamePhase.PEEKING && !diceRevealed));
    const isRevealing = phase === GamePhase.REVEALING || phase === GamePhase.GAME_OVER;
    const rollerExposed = isActiveRoller && diceRevealed && (phase === GamePhase.PEEKING || phase === GamePhase.CLAIMING);
    return { canTap, isRevealing, rollerExposed, showDice: diceValues !== null && (rollerExposed || isRevealing) };
  }

  /** One frame at `t` seconds: the pivot's pose and the glow eased toward their targets, and another frame asked for while moving. */
  frame(t: number, props: IPropsCupMotion, cup: IServiceCupPivot | null, mat: IServiceCupGlow<TColor> | null, invalidate: () => void): void {
    const { phase, isActiveRoller, isShaking } = props;
    const { canTap, isRevealing, rollerExposed } = CupMotion.factsOf(props);
    const a = this.anim;
    const peeking = rollerExposed;
    const targetTiltX = peeking ? PEEK_TILT : 0;
    const targetPivotY = peeking ? PEEK_PIVOT_Y : isRevealing ? PIVOT_REST_Y + REVEAL_LIFT : PIVOT_REST_Y;
    const targetPivotZ = peeking ? PEEK_PIVOT_Z : PIVOT_REST_Z;
    const targetSpinZ = isRevealing ? Math.PI * 0.5 : 0; // reveal flourish

    if (cup) {
      const k = isRevealing ? 0.05 : 0.08;
      a.posY = CupMotion.lerp(a.posY, targetPivotY, k);
      a.posZ = CupMotion.lerp(a.posZ, targetPivotZ, k);
      a.tiltX = CupMotion.lerp(a.tiltX, targetTiltX, k);
      a.spinZ = CupMotion.lerp(a.spinZ, targetSpinZ, k);

      // Shake oscillation (only while rolling) layered on top
      const shakeX = isShaking ? Math.sin(t * 26) * 0.06 : 0;
      const shakeZ = isShaking ? Math.cos(t * 21) * 0.05 : 0;
      const shakeR = isShaking ? Math.sin(t * 30) * 0.05 : 0;

      cup.position.x = CupMotion.lerp(cup.position.x, shakeX, 0.4);
      cup.position.z = a.posZ + shakeZ;
      cup.position.y = a.posY;
      cup.rotation.x = a.tiltX + shakeR;
      cup.rotation.z = a.spinZ;
    }

    // Emissive glow: green pulse when tappable, soft blue for non-roller during play
    if (mat) {
      let targetIntensity = 0;
      let emissiveColor = this.colour('#000000');

      if (canTap) {
        targetIntensity = 0.22 + Math.sin(t * 3.2) * 0.14;
        emissiveColor = this.colour('#10b981');
      } else if (!isActiveRoller && (phase === GamePhase.ROLLING || phase === GamePhase.PEEKING)) {
        targetIntensity = 0.05 + Math.sin(t * 1.5) * 0.03;
        emissiveColor = this.colour('#3b82f6');
      }

      mat.emissive.lerp(emissiveColor, 0.08);
      mat.emissiveIntensity = CupMotion.lerp(mat.emissiveIntensity, targetIntensity, 0.08);
    }

    const pivotSettled =
      Math.abs(a.posY - targetPivotY) < EPS &&
      Math.abs(a.posZ - targetPivotZ) < EPS &&
      Math.abs(a.tiltX - targetTiltX) < EPS &&
      Math.abs(a.spinZ - targetSpinZ) < EPS;
    const glowPulsing = canTap || (!isActiveRoller && (phase === GamePhase.ROLLING || phase === GamePhase.PEEKING));
    if (!pivotSettled || isShaking || glowPulsing) {
      invalidate();
    }
  }

  /** The hand over a cup that can be tapped. */
  readonly pointerOver = (canTap: boolean): void => {
    if (canTap) this.cursor.set('pointer');
  };

  readonly pointerOut = (): void => {
    this.cursor.set('default');
  };

  getSnapshot(): typeof NO_STATE {
    return NO_STATE;
  }

  subscribe(): () => void {
    return () => undefined;
  }

  attach(): void {}

  detach(): void {}
}
